import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  X,
  Plus,
  Trash2,
  Clock,
  Mail,
  User,
  Key,
  ShieldCheck,
  ShieldAlert,
  Users,
  Phone,
  Building2,
  Calendar,
  CheckCircle,
  XCircle,
  Clipboard,
  ClipboardCheck,
  Lock,
  Link,
  UserMinus,
  Cloud,
  Zap,
  Pencil,
  AlertTriangle,
  MapPin,
  Save,
  UserX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import HelpTooltip from '../common/HelpTooltip';
import { DetailedUser, UserGroup, UserRole } from '../../types';
import {
  getUserDetail,
  getTenantGroups,
  assignUserLicense,
  removeUserLicense,
  addUserToGroup,
  removeUserFromGroup,
  resetUserPassword,
  resetUserMfa,
  setUserEnabled,
  sendUserTAP,
  generateUserTAP,
  enableTAP,
  updateUserUPN,
  getTenantDomains,
  getRingCentralStatus,
  storeSecureCredentials,
  sendSecureLinkEmail,
  getDirectoryRoles,
  assignUserRole,
  removeUserRole,
  updateUserProperties,
  deleteUser,
} from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../auth/AuthProvider';
import OffboardWizard from './OffboardWizard';
import QuickSetupWizard from './QuickSetupWizard';
import BatchMailboxEditor from '../Mailboxes/BatchMailboxEditor';
import OneDriveSharing from '../Mailboxes/OneDriveSharing';
import UserSignInHistory from './UserSignInHistory';
import { useClientSyncData } from '../../hooks/useClientSyncData';

interface UserDetailProps {
  clientId: string;
  clientName: string;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated?: () => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function formatCacheAge(isoTimestamp: string | null | undefined): string | null {
  if (!isoTimestamp) return null;
  const diffMs = Date.now() - new Date(isoTimestamp).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function shortenLicenseName(name: string): string {
  return name
    .replace(/^Microsoft 365 /i, 'M365 ')
    .replace(/^Office 365 /i, 'O365 ')
    .replace(/^Enterprise Mobility \+ Security /i, 'EMS ')
    .replace(/^Azure Active Directory /i, 'AAD ')
    .replace(/^Azure AD /i, 'AAD ')
    .replace(/^Microsoft Defender for /i, 'Defender ')
    .replace(/^Microsoft /i, '')
    .replace(/^Dynamics 365 /i, 'D365 ');
}

export default function UserDetail({ clientId, clientName, userId, isOpen, onClose, onUserUpdated }: UserDetailProps) {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<DetailedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allGroups, setAllGroups] = useState<UserGroup[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetMfa, setShowResetMfa] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const [showOffboardWizard, setShowOffboardWizard] = useState(false);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [mfaResetResult, setMfaResetResult] = useState<string[] | null>(null);
  const [showSendTAP, setShowSendTAP] = useState(false);
  const [tapRecipientEmail, setTapRecipientEmail] = useState('');
  const [tapRecipientPhone, setTapRecipientPhone] = useState('');
  const [tapSentResult, setTapSentResult] = useState<{ success: boolean; error?: string; deliveryMethod?: string; smsError?: string } | null>(null);
  // Quick TAP / Advanced TAP state
  const [tapMode, setTapMode] = useState<'quick' | 'advanced'>('quick');
  const [quickTapResult, setQuickTapResult] = useState<{ temporaryAccessPass: string; expiresAt: string; isUsableOnce: boolean } | null>(null);
  const [tapDurationHours, setTapDurationHours] = useState(2);
  const [tapOneTimeUse, setTapOneTimeUse] = useState(true);
  const [tapDelayedStart, setTapDelayedStart] = useState(false);
  const [tapStartDateTime, setTapStartDateTime] = useState('');
  const [tapDeliveryMode, setTapDeliveryMode] = useState<'screen' | 'send'>('screen');
  const [tapCodeCopied, setTapCodeCopied] = useState(false);

  // Change Email / UPN state
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmailUsername, setNewEmailUsername] = useState('');
  const [newEmailDomain, setNewEmailDomain] = useState('');
  const [availableDomains, setAvailableDomains] = useState<{ domain: string; isDefault: boolean }[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [changeEmailResult, setChangeEmailResult] = useState<{ success?: boolean; message?: string; error?: string } | null>(null);

  // Password send states
  const [passwordRecipientEmail, setPasswordRecipientEmail] = useState('');
  const [passwordRecipientPhone, setPasswordRecipientPhone] = useState('');
  const [passwordSentResult, setPasswordSentResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [secureLinkToken, setSecureLinkToken] = useState<string | null>(null);
  const [, setSecureLinkExpiry] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [ringCentralConnected, setRingCentralConnected] = useState(false);

  // Role management states
  const [showAddRole, setShowAddRole] = useState(false);
  const [allRoles, setAllRoles] = useState<UserRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [showConfirmRemoveRole, setShowConfirmRemoveRole] = useState<{ roleId: string; roleName: string } | null>(null);

  // Edit properties states
  const [showEditProperties, setShowEditProperties] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');
  const [editDepartment, setEditDepartment] = useState('');
  const [editMobilePhone, setEditMobilePhone] = useState('');
  const [editOfficeLocation, setEditOfficeLocation] = useState('');

  // Delete user states
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  // Mailbox: show live Exchange data only when user clicks refresh
  const [mailboxLoaded, setMailboxLoaded] = useState(false);

  // Get cached sync data for mailbox info
  const { syncData } = useClientSyncData(clientId);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Form states
  const [selectedLicense, setSelectedLicense] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  // Tab state - using string values for Radix Tabs
  const [activeTab, setActiveTab] = useState('licenses');

  useEffect(() => {
    if (isOpen && userId) {
      loadUser();
      setMailboxLoaded(false); // Reset mailbox lazy-load on each open
      // Check RingCentral status for split-channel delivery
      getRingCentralStatus()
        .then(response => setRingCentralConnected(response.connected))
        .catch(() => setRingCentralConnected(false));
    }
  }, [isOpen, userId, clientId]);

  async function loadUser() {
    setLoading(true);
    setError(null);
    try {
      const [userData, groupsData] = await Promise.all([
        getUserDetail(clientId, userId),
        getTenantGroups(clientId).catch(() => ({ groups: [] })),
      ]);
      setUser(userData);
      setAllGroups(groupsData.groups || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }

  async function handleAssignLicense() {
    if (!selectedLicense || !user) return;
    setActionLoading(true);
    try {
      await assignUserLicense(clientId, userId, selectedLicense);
      toast.success('License assigned successfully');
      setShowAddLicense(false);
      setSelectedLicense('');
      // Small delay to allow Graph API propagation, then refresh both modal and list
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUser();
      await onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign license');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveLicense(skuId: string, skuName: string) {
    if (!confirm(`Remove "${skuName}" license from ${user?.displayName}?`)) return;
    setActionLoading(true);
    try {
      await removeUserLicense(clientId, userId, skuId);
      toast.success('License removed successfully');
      // Small delay to allow Graph API propagation, then refresh both modal and list
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUser();
      await onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove license');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleAddToGroup() {
    if (!selectedGroup) return;
    setActionLoading(true);
    try {
      await addUserToGroup(clientId, userId, selectedGroup);
      toast.success('Added to group successfully');
      setShowAddGroup(false);
      setSelectedGroup('');
      await loadUser();
      onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add to group');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveFromGroup(groupId: string, groupName: string) {
    if (!confirm(`Remove ${user?.displayName} from "${groupName}"?`)) return;
    setActionLoading(true);
    try {
      await removeUserFromGroup(clientId, userId, groupId);
      toast.success('Removed from group successfully');
      await loadUser();
      onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove from group');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleOpenAddRole() {
    setShowAddRole(true);
    setSelectedRole('');
    if (allRoles.length === 0) {
      setRolesLoading(true);
      try {
        const data = await getDirectoryRoles(clientId);
        setAllRoles(data.roles || []);
      } catch (err) {
        toast.error('Failed to load directory roles');
      } finally {
        setRolesLoading(false);
      }
    }
  }

  async function handleAssignRole() {
    if (!selectedRole || !user) return;
    const roleName = allRoles.find(r => r.id === selectedRole)?.displayName || 'Unknown';
    setActionLoading(true);
    try {
      await assignUserRole(clientId, userId, selectedRole, roleName);
      toast.success(`Role "${roleName}" assigned successfully`);
      setShowAddRole(false);
      setSelectedRole('');
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUser();
      onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveRole(roleId: string, roleName: string) {
    setActionLoading(true);
    try {
      await removeUserRole(clientId, userId, roleId, roleName);
      toast.success(`Role "${roleName}" removed successfully`);
      setShowConfirmRemoveRole(null);
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUser();
      onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove role');
    } finally {
      setActionLoading(false);
    }
  }

  // Get roles user does NOT have for the add modal
  const availableRoles = allRoles.filter(
    r => !user?.roles.some(ur => ur.id === r.id)
  );

  // State for password reset success panel
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [copiedPasswordInfo, setCopiedPasswordInfo] = useState(false);

  async function handleResetPassword() {
    setActionLoading(true);
    try {
      const result = await resetUserPassword(clientId, userId);
      if (result.temporaryPassword) {
        setGeneratedPassword(result.temporaryPassword);
        setPasswordResetSuccess(true);
        setPasswordSentResult(null);
        setCopiedPasswordInfo(false);
        setSecureLinkToken(null);
        setSecureLinkExpiry(null);
        setPasswordRecipientPhone('');
      } else {
        toast.success('Password reset successfully');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset password');
      setShowResetPassword(false);
    } finally {
      setActionLoading(false);
    }
  }

  function handleClosePasswordSuccess() {
    setPasswordResetSuccess(false);
    setShowResetPassword(false);
    setGeneratedPassword(null);
    setPasswordSentResult(null);
    setCopiedPasswordInfo(false);
    setPasswordRecipientEmail('');
    setPasswordRecipientPhone('');
    setSecureLinkToken(null);
    setSecureLinkExpiry(null);
    loadUser();
    onUserUpdated?.();
  }

  function getSecureLink() {
    if (!secureLinkToken) return '';
    return `${window.location.origin}/credentials/${secureLinkToken}`;
  }

  function copyPasswordCredentials() {
    if (!user || !generatedPassword) return;
    const lines = [
      `Account Credentials for ${user.displayName}`,
      ``,
      `Email: ${user.userPrincipalName}`,
    ];
    if (secureLinkToken) {
      lines.push(`View Credentials: ${getSecureLink()}`);
      lines.push(`(This link can only be used once)`);
    } else {
      lines.push(`Temporary Password: ${generatedPassword}`);
    }
    lines.push(``, `Sign in at: https://portal.office.com`, `You will be prompted to change your password on first sign-in.`);
    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedPasswordInfo(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedPasswordInfo(false), 3000);
  }

  async function handleSendSecureLink() {
    if (!passwordRecipientEmail || !user || !generatedPassword) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setGeneratingLink(true);
    setActionLoading(true);
    try {
      // Step 1: Store credentials securely (with phone for SMS verification if provided)
      const storeResult = await storeSecureCredentials({
        userName: user.displayName,
        userEmail: user.userPrincipalName,
        tempPassword: generatedPassword,
        clientName,
        ...(passwordRecipientPhone ? {
          phoneNumber: passwordRecipientPhone,
          senderUserId: authUser?.userId,
        } : {}),
      });

      if (!storeResult.success || !storeResult.token || !storeResult.expiresAt) {
        throw new Error(storeResult.error || 'Failed to generate secure link');
      }

      const secureLink = `${window.location.origin}/credentials/${storeResult.token}`;
      setSecureLinkToken(storeResult.token);
      setSecureLinkExpiry(storeResult.expiresAt);

      // Step 2: Send the secure link via email
      const result = await sendSecureLinkEmail({
        recipientEmail: passwordRecipientEmail,
        userName: user.displayName,
        userEmail: user.userPrincipalName,
        secureLink,
        expiresAt: storeResult.expiresAt,
        clientName,
      });
      if (result.success) {
        setPasswordSentResult({ success: true });
        toast.success(`Secure link sent to ${passwordRecipientEmail}`);
      } else {
        setPasswordSentResult({ success: false, error: result.error || 'Failed to send email' });
        toast.error(result.error || 'Failed to send email');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to send secure link';
      setPasswordSentResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setGeneratingLink(false);
      setActionLoading(false);
    }
  }

  async function handleResetMfa() {
    setActionLoading(true);
    try {
      const result = await resetUserMfa(clientId, userId);
      if (result.success) {
        setMfaResetResult(result.deletedMethods || []);
        toast.success(result.message || 'MFA reset successfully');
        await loadUser();
        onUserUpdated?.();
      } else {
        toast.error(result.error || 'Failed to reset MFA');
        setShowResetMfa(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset MFA');
      setShowResetMfa(false);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendTAP() {
    if (!tapRecipientEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setActionLoading(true);
    try {
      // First ensure TAP is enabled in the tenant
      const enableResult = await enableTAP(clientId);
      if (!enableResult.success && !enableResult.alreadyEnabled) {
        if (enableResult.needsReconsent) {
          setTapSentResult({ success: false, error: 'TAP permission not granted. Customer needs to re-consent the app.' });
        } else {
          setTapSentResult({ success: false, error: enableResult.error || 'Could not enable TAP in tenant' });
        }
        setActionLoading(false);
        return;
      }

      // Now send the TAP (pass advanced options when in advanced mode)
      const result = await sendUserTAP(
        clientId, userId, tapRecipientEmail, tapRecipientPhone || undefined,
        tapMode === 'advanced' ? tapDurationHours * 60 : undefined,
        tapMode === 'advanced' ? tapOneTimeUse : undefined,
        tapMode === 'advanced' && tapDelayedStart && tapStartDateTime ? tapStartDateTime : undefined
      );
      if (result.success) {
        setTapSentResult({
          success: true,
          deliveryMethod: result.deliveryMethod,
          smsError: result.smsError,
        });
        if (result.deliveryMethod === 'split') {
          toast.success(`TAP sent via SMS, instructions sent to ${tapRecipientEmail}`);
        } else {
          toast.success(`TAP sent to ${tapRecipientEmail}`);
        }
      } else {
        setTapSentResult({ success: false, error: result.error || 'Failed to send TAP' });
        toast.error(result.error || 'Failed to send TAP');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to send TAP';
      setTapSentResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleGenerateTAP(isQuick: boolean) {
    setActionLoading(true);
    setQuickTapResult(null);
    setTapCodeCopied(false);
    try {
      // First ensure TAP is enabled in the tenant
      const enableResult = await enableTAP(clientId);
      if (!enableResult.success && !enableResult.alreadyEnabled) {
        if (enableResult.needsReconsent) {
          toast.error('TAP permission not granted. Customer needs to re-consent the app.');
        } else {
          toast.error(enableResult.error || 'Could not enable TAP in tenant');
        }
        setActionLoading(false);
        return;
      }

      const options = isQuick
        ? { lifetimeInMinutes: 120, isUsableOnce: true }
        : {
            lifetimeInMinutes: tapDurationHours * 60,
            isUsableOnce: tapOneTimeUse,
            ...(tapDelayedStart && tapStartDateTime ? { startDateTime: tapStartDateTime } : {})
          };

      const result = await generateUserTAP(clientId, userId, options);
      if (result.success && result.temporaryAccessPass) {
        setQuickTapResult({
          temporaryAccessPass: result.temporaryAccessPass,
          expiresAt: result.expiresAt || '',
          isUsableOnce: result.isUsableOnce ?? true,
        });
        toast.success('TAP generated successfully');
      } else {
        toast.error(result.error || 'Failed to generate TAP');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate TAP';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleOpenChangeEmail() {
    setShowChangeEmail(true);
    setChangeEmailResult(null);
    // Pre-fill current username
    if (user?.userPrincipalName) {
      const [username, domain] = user.userPrincipalName.split('@');
      setNewEmailUsername(username);
      setNewEmailDomain(domain);
    }
    // Fetch available domains
    if (availableDomains.length === 0) {
      setDomainsLoading(true);
      try {
        const result = await getTenantDomains(clientId);
        if (result.domains) {
          setAvailableDomains(result.domains);
          // Set the current domain as selected if available
          if (user?.userPrincipalName) {
            const currentDomain = user.userPrincipalName.split('@')[1];
            setNewEmailDomain(currentDomain);
          }
        }
      } catch {
        toast.error('Failed to load tenant domains');
      } finally {
        setDomainsLoading(false);
      }
    }
  }

  async function handleChangeEmail() {
    if (!newEmailUsername || !newEmailDomain) {
      toast.error('Please enter a username and select a domain');
      return;
    }
    const newUPN = `${newEmailUsername}@${newEmailDomain}`;
    if (user?.userPrincipalName && newUPN.toLowerCase() === user.userPrincipalName.toLowerCase()) {
      toast.error('New email is the same as the current one');
      return;
    }
    setActionLoading(true);
    setChangeEmailResult(null);
    try {
      const result = await updateUserUPN(clientId, userId, newUPN);
      if (result.success) {
        setChangeEmailResult({ success: true, message: result.message });
        toast.success(`Email changed to ${newUPN}`);
        // Refresh user data
        const refreshed = await getUserDetail(clientId, userId);
        if (refreshed) setUser(refreshed);
      } else {
        setChangeEmailResult({ error: result.error || 'Failed to change email' });
        toast.error(result.error || 'Failed to change email');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to change email';
      setChangeEmailResult({ error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleToggleEnabled() {
    if (!user) return;
    setActionLoading(true);
    try {
      await setUserEnabled(clientId, userId, !user.accountEnabled);
      toast.success(`Account ${user.accountEnabled ? 'disabled' : 'enabled'} successfully`);
      setShowConfirmDisable(false);
      await loadUser();
      onUserUpdated?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update account');
    } finally {
      setActionLoading(false);
    }
  }

  function handleOpenEditProperties() {
    if (!user) return;
    setEditDisplayName(user.displayName || '');
    setEditJobTitle(user.jobTitle || '');
    setEditDepartment(user.department || '');
    setEditMobilePhone(user.mobilePhone || '');
    setEditOfficeLocation(user.officeLocation || '');
    setShowEditProperties(true);
  }

  async function handleSaveProperties() {
    if (!user) return;
    setActionLoading(true);
    try {
      const properties: Record<string, string> = {};
      if (editDisplayName !== (user.displayName || '')) properties.displayName = editDisplayName;
      if (editJobTitle !== (user.jobTitle || '')) properties.jobTitle = editJobTitle;
      if (editDepartment !== (user.department || '')) properties.department = editDepartment;
      if (editMobilePhone !== (user.mobilePhone || '')) properties.mobilePhone = editMobilePhone;
      if (editOfficeLocation !== (user.officeLocation || '')) properties.officeLocation = editOfficeLocation;

      if (Object.keys(properties).length === 0) {
        toast.error('No changes to save');
        setActionLoading(false);
        return;
      }

      const result = await updateUserProperties(clientId, userId, properties);
      if (result.success) {
        toast.success('User properties updated successfully');
        setShowEditProperties(false);
        await loadUser();
        onUserUpdated?.();
      } else {
        toast.error(result.error || 'Failed to update properties');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update properties');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteUser() {
    if (!user) return;
    setActionLoading(true);
    try {
      const result = await deleteUser(clientId, userId);
      if (result.success) {
        toast.success(`User ${user.displayName} has been permanently deleted`);
        setShowDeleteUser(false);
        onClose();
        onUserUpdated?.();
      } else {
        toast.error(result.error || 'Failed to delete user');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  }

  // Get groups user is NOT in for the add modal
  const availableGroups = allGroups.filter(
    g => !user?.groups.some(ug => ug.id === g.id)
  );

  const tabs = [
    { value: 'licenses', name: 'Licenses', icon: Key, help: 'View and manage licenses assigned to this user. Assign new licenses or remove existing ones.' },
    { value: 'groups', name: 'Groups', icon: Users, help: 'View group memberships. Add or remove the user from Security, M365, or Distribution groups.' },
    { value: 'roles', name: 'Roles', icon: ShieldCheck, help: 'View and manage Azure AD directory roles (admin roles) assigned to this user. Add or remove roles like Global Admin, Exchange Admin, etc.' },
    { value: 'signins', name: 'Sign-Ins', icon: Clock, help: 'View recent sign-in history with location, device, and anomaly detection. Shows risky sign-ins, failed logins, and unusual activity.' },
    { value: 'mailbox', name: 'Mailbox', icon: Mail, help: 'Manage mailbox delegation: grant or revoke Full Access, Send As, and Send on Behalf permissions.' },
    { value: 'onedrive', name: 'OneDrive', icon: Cloud, help: 'Manage OneDrive sharing: view current shares, grant access to others, or revoke existing access.' },
    { value: 'actions', name: 'Actions', icon: User, help: 'Perform user management actions: reset password, enable/disable account, or start the offboarding process.' },
  ];

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="bg-muted px-4 py-4 sm:px-6 border-b border-border rounded-t-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 h-14 w-14 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <span className="text-primary-700 dark:text-primary-300 font-semibold text-lg">
                  {user?.displayName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '??'}
                </span>
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground flex items-center">
                  {user?.displayName || 'Loading...'}
                  {user && (
                    <span className={classNames(
                      'ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      user.accountEnabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    )}>
                      {user.accountEnabled ? 'Active' : 'Disabled'}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">View and manage user details, licenses, groups, and actions</DialogDescription>
                {user && (
                  <p className="text-sm text-muted-foreground">
                    {user.userPrincipalName}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4 sm:px-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          ) : user ? (
            <>
              {/* User Info Summary */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Profile</h4>
                  {!showEditProperties && (
                    <button
                      onClick={handleOpenEditProperties}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-muted-foreground hover:text-foreground hover:bg-accent"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </button>
                  )}
                </div>

                {showEditProperties ? (
                  <div className="p-4 bg-muted rounded-lg border border-border space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Display Name</label>
                      <input
                        type="text"
                        value={editDisplayName}
                        onChange={(e) => setEditDisplayName(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Job Title</label>
                        <input
                          type="text"
                          value={editJobTitle}
                          onChange={(e) => setEditJobTitle(e.target.value)}
                          placeholder="e.g. IT Manager"
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Department</label>
                        <input
                          type="text"
                          value={editDepartment}
                          onChange={(e) => setEditDepartment(e.target.value)}
                          placeholder="e.g. Engineering"
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Mobile Phone</label>
                        <input
                          type="tel"
                          value={editMobilePhone}
                          onChange={(e) => setEditMobilePhone(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Office Location</label>
                        <input
                          type="text"
                          value={editOfficeLocation}
                          onChange={(e) => setEditOfficeLocation(e.target.value)}
                          placeholder="e.g. Building A, Room 101"
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 pt-2">
                      <button
                        onClick={() => setShowEditProperties(false)}
                        className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProperties}
                        disabled={actionLoading}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5 mr-1" />
                        {actionLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {user.mail && (
                      <div className="flex items-center text-muted-foreground">
                        <Mail className="h-4 w-4 mr-2" />
                        {user.mail}
                      </div>
                    )}
                    {user.mobilePhone && (
                      <div className="flex items-center text-muted-foreground">
                        <Phone className="h-4 w-4 mr-2" />
                        {user.mobilePhone}
                      </div>
                    )}
                    {user.jobTitle && (
                      <div className="flex items-center text-muted-foreground">
                        <User className="h-4 w-4 mr-2" />
                        {user.jobTitle}
                      </div>
                    )}
                    {user.department && (
                      <div className="flex items-center text-muted-foreground">
                        <Building2 className="h-4 w-4 mr-2" />
                        {user.department}
                      </div>
                    )}
                    {user.officeLocation && (
                      <div className="flex items-center text-muted-foreground">
                        <MapPin className="h-4 w-4 mr-2" />
                        {user.officeLocation}
                      </div>
                    )}
                    {user.createdDateTime && (
                      <div className="flex items-center text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-2" />
                        Created: {format(new Date(user.createdDateTime), 'MMM d, yyyy')}
                      </div>
                    )}
                    {user.signInActivity?.lastSignInDateTime && (
                      <div className="flex items-center text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-2" />
                        Last sign-in: {format(new Date(user.signInActivity.lastSignInDateTime), 'MMM d, yyyy')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Quick Setup Button */}
              <div className="mb-4">
                <button
                  onClick={() => setShowQuickSetup(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 shadow-sm"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Quick Setup — Enable, License, Password &amp; Send
                </button>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto no-scrollbar -mx-4 sm:-mx-6 px-4 sm:px-6">
                  <TabsList className="inline-flex w-max rounded-lg bg-muted p-1 h-auto gap-0.5">
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="rounded-md px-2.5 py-1.5 text-xs font-medium leading-5 inline-flex items-center justify-center focus:outline-none data-[state=active]:bg-card data-[state=active]:shadow data-[state=active]:text-primary-700 dark:data-[state=active]:text-primary-300 text-muted-foreground hover:bg-white/[0.12] hover:text-foreground"
                        title={tab.help}
                      >
                        <tab.icon className="h-3.5 w-3.5 mr-1" />
                        {tab.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {/* Licenses Tab */}
                <TabsContent value="licenses" className="mt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium text-foreground">
                        Assigned Licenses ({user.assignedLicenses.length})
                      </h4>
                      <button
                        onClick={() => setShowAddLicense(true)}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add License
                      </button>
                    </div>

                    {user.assignedLicenses.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No licenses assigned</p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {user.assignedLicenses.map((lic) => (
                          <li key={lic.skuId} className="py-2 flex justify-between items-center">
                            <span className="text-sm text-foreground" title={lic.skuName}>
                              {shortenLicenseName(lic.skuName)}
                            </span>
                            <button
                              onClick={() => handleRemoveLicense(lic.skuId, lic.skuName)}
                              disabled={actionLoading}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Add License Modal */}
                    {showAddLicense && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <h5 className="text-sm font-medium text-foreground mb-2">Assign License</h5>
                        <select
                          value={selectedLicense}
                          onChange={(e) => setSelectedLicense(e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                        >
                          <option value="">Select a license...</option>
                          {user.availableLicenses.map((lic) => (
                            <option key={lic.skuId} value={lic.skuId}>
                              {shortenLicenseName(lic.skuName)} ({lic.available} available)
                            </option>
                          ))}
                        </select>
                        <div className="mt-3 flex justify-end space-x-2">
                          <button
                            onClick={() => { setShowAddLicense(false); setSelectedLicense(''); }}
                            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAssignLicense}
                            disabled={!selectedLicense || actionLoading}
                            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                          >
                            {actionLoading ? 'Assigning...' : 'Assign'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Groups Tab */}
                <TabsContent value="groups" className="mt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-foreground">
                          Group Memberships ({user.groups.length})
                        </h4>
                        {user.cachedAt?.groups && (
                          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" title={`Cached data from ${new Date(user.cachedAt.groups).toLocaleString()}`}>
                            {formatCacheAge(user.cachedAt.groups)}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowAddGroup(true)}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add to Group
                      </button>
                    </div>

                    {user.groups.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No group memberships</p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {user.groups.map((group) => (
                          <li key={group.id} className="py-2 flex justify-between items-center">
                            <div>
                              <span className="text-sm text-foreground">{group.displayName}</span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({group.groupType})
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveFromGroup(group.id, group.displayName)}
                              disabled={actionLoading}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Add to Group Modal */}
                    {showAddGroup && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <h5 className="text-sm font-medium text-foreground mb-2">Add to Group</h5>
                        <select
                          value={selectedGroup}
                          onChange={(e) => setSelectedGroup(e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                        >
                          <option value="">Select a group...</option>
                          {availableGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.displayName} ({group.groupType})
                            </option>
                          ))}
                        </select>
                        <div className="mt-3 flex justify-end space-x-2">
                          <button
                            onClick={() => { setShowAddGroup(false); setSelectedGroup(''); }}
                            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddToGroup}
                            disabled={!selectedGroup || actionLoading}
                            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                          >
                            {actionLoading ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Roles Tab */}
                <TabsContent value="roles" className="mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-foreground">
                          Directory Roles ({user.roles.length})
                        </h4>
                        {user.cachedAt?.roles && (
                          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" title={`Cached data from ${new Date(user.cachedAt.roles).toLocaleString()}`}>
                            {formatCacheAge(user.cachedAt.roles)}
                          </span>
                        )}
                      </div>
                      {!showAddRole && (
                        <button
                          onClick={handleOpenAddRole}
                          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add Role
                        </button>
                      )}
                    </div>

                    {user.roles.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No admin roles assigned</p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {user.roles.map((role) => (
                          <li key={role.id} className="py-2 flex justify-between items-start">
                            <div>
                              <div className="flex items-center">
                                <ShieldCheck className="h-5 w-5 text-amber-500 mr-2" />
                                <span className="text-sm font-medium text-foreground">
                                  {role.displayName}
                                </span>
                              </div>
                              {role.description && (
                                <p className="ml-7 text-xs text-muted-foreground mt-0.5">
                                  {role.description}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => setShowConfirmRemoveRole({ roleId: role.id, roleName: role.displayName })}
                              disabled={actionLoading}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 mt-0.5"
                              title={`Remove ${role.displayName} role`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Confirm Remove Role Dialog */}
                    {showConfirmRemoveRole && (
                      <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-start gap-2">
                          <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="text-sm font-medium text-red-800 dark:text-red-300">
                              Remove Directory Role
                            </h5>
                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                              Are you sure you want to remove the <strong>{showConfirmRemoveRole.roleName}</strong> role from {user.displayName}?
                              {showConfirmRemoveRole.roleName.toLowerCase().includes('global admin') && (
                                <span className="block mt-1 font-semibold">
                                  Warning: This is a Global Administrator role. Removing it will revoke all administrative privileges.
                                </span>
                              )}
                            </p>
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => setShowConfirmRemoveRole(null)}
                                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleRemoveRole(showConfirmRemoveRole.roleId, showConfirmRemoveRole.roleName)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                              >
                                {actionLoading ? 'Removing...' : 'Remove Role'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Role Modal */}
                    {showAddRole && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <h5 className="text-sm font-medium text-foreground mb-2">Add Directory Role</h5>
                        {rolesLoading ? (
                          <p className="text-sm text-muted-foreground">Loading available roles...</p>
                        ) : (
                          <>
                            <select
                              value={selectedRole}
                              onChange={(e) => setSelectedRole(e.target.value)}
                              className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                            >
                              <option value="">Select a role...</option>
                              {availableRoles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.displayName}
                                </option>
                              ))}
                            </select>
                            {selectedRole && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {allRoles.find(r => r.id === selectedRole)?.description || ''}
                              </p>
                            )}
                            <div className="mt-3 flex justify-end space-x-2">
                              <button
                                onClick={() => { setShowAddRole(false); setSelectedRole(''); }}
                                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleAssignRole}
                                disabled={!selectedRole || actionLoading}
                                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                              >
                                {actionLoading ? 'Assigning...' : 'Assign Role'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Sign-Ins Tab */}
                <TabsContent value="signins" className="mt-4">
                  {user.cachedAt?.signIns && (
                    <div className="mb-2 flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" title={`Cached data from ${new Date(user.cachedAt.signIns).toLocaleString()}`}>
                        Forensic data: {formatCacheAge(user.cachedAt.signIns)}
                      </span>
                    </div>
                  )}
                  <UserSignInHistory
                    clientId={clientId}
                    userId={userId}
                    userDisplayName={user.displayName}
                  />
                </TabsContent>

                {/* Mailbox Tab — reads from cached sync data, live Exchange only on demand */}
                <TabsContent value="mailbox" className="mt-4 space-y-4">
                  {(() => {
                    const upn = user.userPrincipalName.toLowerCase();
                    const cachedUserMailbox = syncData?.userMailboxes?.data?.find(
                      m => m.email.toLowerCase() === upn
                    );
                    const cachedSharedMailbox = syncData?.sharedMailboxes?.data?.find(
                      m => m.email.toLowerCase() === upn
                    );
                    const hasCachedData = !!cachedUserMailbox || !!cachedSharedMailbox;
                    const cacheTime = syncData?.userMailboxes?.lastSynced || syncData?.sharedMailboxes?.lastSynced;

                    return (
                      <>
                        {/* Cached mailbox info — instant */}
                        {hasCachedData && (
                          <>
                            {cacheTime && (
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  Cached: {formatCacheAge(cacheTime)}
                                </span>
                              </div>
                            )}

                            {/* Mailbox overview */}
                            <div className="bg-card border border-border rounded-lg p-4">
                              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Mailbox Info
                              </h4>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {cachedUserMailbox && (
                                  <>
                                    <div>
                                      <span className="text-muted-foreground">Type:</span>{' '}
                                      <span className="text-foreground">{cachedUserMailbox.mailboxType}</span>
                                    </div>
                                    {cachedUserMailbox.totalSizeMB != null && (
                                      <div>
                                        <span className="text-muted-foreground">Size:</span>{' '}
                                        <span className="text-foreground">{cachedUserMailbox.totalSizeMB >= 1024 ? `${(cachedUserMailbox.totalSizeMB / 1024).toFixed(1)} GB` : `${cachedUserMailbox.totalSizeMB} MB`}</span>
                                      </div>
                                    )}
                                    {cachedUserMailbox.itemCount != null && (
                                      <div>
                                        <span className="text-muted-foreground">Items:</span>{' '}
                                        <span className="text-foreground">{cachedUserMailbox.itemCount.toLocaleString()}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">Archive:</span>{' '}
                                      <span className={cachedUserMailbox.archiveEnabled ? 'text-green-600' : 'text-muted-foreground'}>
                                        {cachedUserMailbox.archiveEnabled ? `Enabled${cachedUserMailbox.archiveSizeMB ? ` (${cachedUserMailbox.archiveSizeMB >= 1024 ? `${(cachedUserMailbox.archiveSizeMB / 1024).toFixed(1)} GB` : `${cachedUserMailbox.archiveSizeMB} MB`})` : ''}` : 'Disabled'}
                                      </span>
                                    </div>
                                    {cachedUserMailbox.retentionPolicy && (
                                      <div>
                                        <span className="text-muted-foreground">Retention:</span>{' '}
                                        <span className="text-foreground">{cachedUserMailbox.retentionPolicy}</span>
                                      </div>
                                    )}
                                    {cachedUserMailbox.lastLogonTime && (
                                      <div>
                                        <span className="text-muted-foreground">Last Logon:</span>{' '}
                                        <span className="text-foreground">{new Date(cachedUserMailbox.lastLogonTime).toLocaleDateString()}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Forwarding from cache */}
                            {cachedUserMailbox && (
                              <div className="bg-card border border-border rounded-lg p-4">
                                <h4 className="text-sm font-medium text-foreground mb-2">Mail Forwarding</h4>
                                {cachedUserMailbox.forwardingAddress ? (
                                  <div className="text-sm">
                                    <span className="text-amber-600 font-medium">Forwarding to: </span>
                                    <span className="text-foreground">{cachedUserMailbox.forwardingAddress}</span>
                                    {cachedUserMailbox.deliverAndForward && (
                                      <span className="text-muted-foreground ml-2">(deliver & forward)</span>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground">No forwarding configured</p>
                                )}
                              </div>
                            )}

                            {/* Auto-reply from cache */}
                            {cachedUserMailbox && (
                              <div className="bg-card border border-border rounded-lg p-4">
                                <h4 className="text-sm font-medium text-foreground mb-2">Out of Office</h4>
                                <p className="text-sm">
                                  {cachedUserMailbox.autoReplyEnabled ? (
                                    <span className="text-amber-600 font-medium">Auto-reply is enabled</span>
                                  ) : (
                                    <span className="text-muted-foreground">Auto-reply is off</span>
                                  )}
                                </p>
                              </div>
                            )}

                            {/* Shared mailbox permissions from cache */}
                            {cachedSharedMailbox?.permissions && cachedSharedMailbox.permissions.length > 0 && (
                              <div className="bg-card border border-border rounded-lg p-4">
                                <h4 className="text-sm font-medium text-foreground mb-3">Mailbox Permissions</h4>
                                <div className="space-y-2">
                                  {cachedSharedMailbox.permissions.map((p, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                                      <span className="text-foreground">{p.delegate}</span>
                                      <div className="flex gap-2">
                                        {p.fullAccess && <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">Full Access</span>}
                                        {p.sendAs && <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">Send As</span>}
                                        {p.sendOnBehalf && <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">Send on Behalf</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* No cached data — show message */}
                        {!hasCachedData && !mailboxLoaded && (
                          <div className="text-center py-6">
                            <Mail className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-1">
                              No cached mailbox data available
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              Mailbox data is collected during the 24-hour sync. Click below to load live data from Exchange.
                            </p>
                          </div>
                        )}

                        {/* Batch mailbox editor — all changes in one runbook call */}
                        <BatchMailboxEditor
                          clientId={clientId}
                          userId={userId}
                          userEmail={user.userPrincipalName}
                          userDisplayName={user.displayName}
                          cachedForwarding={cachedUserMailbox?.forwardingAddress}
                          cachedAutoReplyEnabled={cachedUserMailbox?.autoReplyEnabled}
                          cachedDelegates={cachedSharedMailbox?.permissions}
                        />
                      </>
                    );
                  })()}
                </TabsContent>

                {/* OneDrive Tab */}
                <TabsContent value="onedrive" className="mt-4">
                  <OneDriveSharing
                    clientId={clientId}
                    userId={userId}
                    userDisplayName={user.displayName}
                  />
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions" className="mt-4">
                  <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                    {/* Password Reset */}
                    <div className="px-4 py-3 bg-card">
                      {passwordResetSuccess && generatedPassword ? (
                        /* ===== PASSWORD RESET SUCCESS PANEL ===== */
                        <div className="space-y-4">
                          {/* Success Header */}
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-6 w-6 text-green-500" />
                            <h4 className="text-base font-semibold text-foreground">Password Reset Successfully</h4>
                          </div>

                          {/* Account Info Card */}
                          <div className="bg-muted rounded-lg border border-border overflow-hidden">
                            <div className="px-4 py-2.5 bg-muted border-b border-border">
                              <h5 className="text-sm font-medium text-foreground">Account Information</h5>
                            </div>
                            <div className="p-4 space-y-2.5">
                              <div className="grid grid-cols-3 gap-2">
                                <span className="text-sm text-muted-foreground">Name</span>
                                <span className="col-span-2 text-sm font-medium text-foreground">{user?.displayName}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <span className="text-sm text-muted-foreground">Email</span>
                                <span className="col-span-2 text-sm font-medium text-foreground font-mono">{user?.userPrincipalName}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <span className="text-sm text-muted-foreground">Password</span>
                                <span className="col-span-2 text-sm font-medium text-foreground font-mono bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">{generatedPassword}</span>
                              </div>
                            </div>
                          </div>

                          {/* Sign-in Info */}
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-3">
                            <div className="flex items-start">
                              <svg className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="ml-3">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                  <span className="font-medium">Sign-in URL:</span>{' '}
                                  <a href="https://portal.office.com" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                                    https://portal.office.com
                                  </a>
                                </p>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                  User will be prompted to change password on first sign-in.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Share Credentials Section */}
                          <div className="space-y-3">
                            <h5 className="text-sm font-medium text-muted-foreground">Share Credentials</h5>

                            {/* Copy to Clipboard */}
                            <button
                              type="button"
                              onClick={copyPasswordCredentials}
                              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                                copiedPasswordInfo
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                                  : 'bg-card border-border text-muted-foreground hover:bg-accent hover:border-border'
                              }`}
                            >
                              {copiedPasswordInfo ? (
                                <ClipboardCheck className="h-5 w-5 flex-shrink-0" />
                              ) : (
                                <Clipboard className="h-5 w-5 flex-shrink-0" />
                              )}
                              <div className="text-left">
                                <div className="font-medium">{copiedPasswordInfo ? 'Copied!' : 'Copy to Clipboard'}</div>
                                <div className="text-xs text-muted-foreground">
                                  {secureLinkToken ? 'Copy account details with secure link' : 'Copy account details to paste manually'}
                                </div>
                              </div>
                            </button>

                            {/* Send Secure Link */}
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                                <Lock className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                                <div className="text-left flex-1">
                                  <div className="font-medium text-muted-foreground">Send Secure Link</div>
                                  <div className="text-xs text-muted-foreground">
                                    One-time link via email — password never sent in email
                                  </div>
                                </div>
                              </div>
                              {generatingLink ? (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                    Generating secure link and sending...
                                  </div>
                                </div>
                              ) : passwordSentResult?.success ? (
                                <div className="p-4">
                                  <div className="flex items-center text-green-600 dark:text-green-400 mb-2">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span className="font-medium">Secure link sent!</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    Sent to <strong>{passwordRecipientEmail}</strong>
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {passwordRecipientPhone
                                      ? 'Recipient clicks link, enters SMS verification code, views credentials. Link self-destructs after viewing.'
                                      : 'Recipient must verify their identity to view credentials. Link expires in 24 hours and self-destructs after viewing.'}
                                  </p>
                                </div>
                              ) : passwordSentResult?.error ? (
                                <div className="p-4">
                                  <div className="flex items-center text-red-600 dark:text-red-400 mb-2">
                                    <XCircle className="h-5 w-5 mr-2" />
                                    <span className="text-sm">{passwordSentResult.error}</span>
                                  </div>
                                  <button
                                    onClick={() => setPasswordSentResult(null)}
                                    className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-md hover:bg-accent"
                                  >
                                    Try Again
                                  </button>
                                </div>
                              ) : (
                                <div className="p-4 space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                                      Recipient Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="email"
                                      value={passwordRecipientEmail}
                                      onChange={(e) => setPasswordRecipientEmail(e.target.value)}
                                      placeholder="admin@example.com or user's personal email"
                                      className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-green-500"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      Email receives a one-time link (no password in email)
                                    </p>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                                      Phone Number for SMS Verification
                                      {ringCentralConnected ? (
                                        <span className="ml-1 text-xs font-normal text-green-600 dark:text-green-400">(SMS Ready)</span>
                                      ) : (
                                        <span className="ml-1 text-xs font-normal text-yellow-600 dark:text-yellow-400">(RingCentral not connected)</span>
                                      )}
                                    </label>
                                    <input
                                      type="tel"
                                      value={passwordRecipientPhone}
                                      onChange={(e) => setPasswordRecipientPhone(e.target.value)}
                                      placeholder={user?.mobilePhone || '+1 (555) 123-4567'}
                                      className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-green-500"
                                    />
                                    {ringCentralConnected ? (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        Verification code sent via SMS — user can&apos;t access their email yet!
                                      </p>
                                    ) : (
                                      <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                                        Connect RingCentral in My Settings (user menu) to enable SMS verification. Without it, verification falls back to email.
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={handleSendSecureLink}
                                    disabled={actionLoading || !passwordRecipientEmail}
                                    className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                                  >
                                    <Link className="h-4 w-4" />
                                    {actionLoading ? 'Sending...' : 'Send Secure Link'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Close Button */}
                          <div className="flex justify-end pt-2 border-t border-border">
                            <button
                              onClick={handleClosePasswordSuccess}
                              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-md hover:bg-accent"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ===== RESET PASSWORD BUTTON & CONFIRMATION ===== */
                        <>
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                                Reset Password
                                <HelpTooltip text="Generates a random temporary password. User must change it on next sign-in. Does NOT disable the account or revoke sessions - user can still access data until they're signed out." />
                              </h4>
                              <p className="text-xs text-muted-foreground mt-1">
                                Generate a temporary password for the user
                              </p>
                            </div>
                            <button
                              onClick={() => { setShowResetPassword(true); setGeneratedPassword(null); setPasswordResetSuccess(false); }}
                              className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 rounded-md hover:bg-amber-200 dark:hover:bg-amber-800"
                            >
                              Reset Password
                            </button>
                          </div>

                          {showResetPassword && (
                            <div className="mt-4 p-3 bg-muted rounded-lg space-y-3">
                              <p className="text-sm text-muted-foreground">
                                This will generate a random temporary password. The user will be required to change it on next sign-in.
                              </p>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => setShowResetPassword(false)}
                                  className="px-3 py-1.5 text-sm text-muted-foreground"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleResetPassword}
                                  disabled={actionLoading}
                                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
                                >
                                  {actionLoading ? 'Resetting...' : 'Reset Password'}
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* MFA Reset */}
                    <div className="px-4 py-3 bg-card">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                            Reset MFA
                            <HelpTooltip text="Removes all MFA authentication methods (Authenticator app, phone, security keys) for this user. They will be prompted to re-register MFA on next sign-in." />
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Remove all MFA methods so user can re-register
                          </p>
                        </div>
                        <button
                          onClick={() => { setShowResetMfa(true); setMfaResetResult(null); }}
                          className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-800 inline-flex items-center"
                        >
                          <ShieldAlert className="h-4 w-4 mr-1" />
                          Reset MFA
                        </button>
                      </div>

                      {showResetMfa && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          {mfaResetResult !== null ? (
                            <div>
                              {mfaResetResult.length > 0 ? (
                                <>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    Successfully removed the following MFA methods:
                                  </p>
                                  <ul className="list-disc list-inside text-sm text-muted-foreground mb-3">
                                    {mfaResetResult.map((method, i) => (
                                      <li key={i}>{method}</li>
                                    ))}
                                  </ul>
                                  <p className="text-xs text-muted-foreground">
                                    The user will be prompted to re-register MFA on their next sign-in.
                                  </p>
                                </>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  No MFA methods were found for this user.
                                </p>
                              )}
                              <button
                                onClick={() => { setShowResetMfa(false); setMfaResetResult(null); }}
                                className="mt-3 text-sm text-muted-foreground hover:text-foreground"
                              >
                                Close
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-muted-foreground mb-3">
                                This will remove all MFA authentication methods for {user?.displayName}.
                                They will need to re-register MFA on their next sign-in.
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                                ⚠️ This action cannot be undone. The user will be temporarily less secure until they re-register MFA.
                              </p>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => setShowResetMfa(false)}
                                  className="px-3 py-1.5 text-sm text-muted-foreground"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleResetMfa}
                                  disabled={actionLoading}
                                  className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                                >
                                  {actionLoading ? 'Resetting...' : 'Reset MFA'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Temporary Access Pass */}
                    <div className="px-4 py-3 bg-card">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                            Temporary Access Pass
                            <HelpTooltip text="Generate a TAP to read over the phone (Quick TAP) or configure advanced options like duration, one-time use, and delayed start (Advanced TAP). Can also send via email/SMS." />
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Generate or send a TAP code
                          </p>
                        </div>
                        <button
                          onClick={() => { setShowSendTAP(!showSendTAP); setTapSentResult(null); setQuickTapResult(null); setTapRecipientEmail(''); setTapRecipientPhone(''); setTapCodeCopied(false); }}
                          className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-800 inline-flex items-center"
                        >
                          <Key className="h-4 w-4 mr-1" />
                          TAP
                        </button>
                      </div>

                      {showSendTAP && (
                        <div className="mt-4">
                          {/* Tab Selector */}
                          <div className="flex border-b border-border mb-4">
                            <button
                              onClick={() => { setTapMode('quick'); setQuickTapResult(null); setTapSentResult(null); setTapCodeCopied(false); }}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                tapMode === 'quick'
                                  ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                                  : 'border-transparent text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <Zap className="h-3.5 w-3.5 inline mr-1" />
                              Quick TAP
                            </button>
                            <button
                              onClick={() => { setTapMode('advanced'); setQuickTapResult(null); setTapSentResult(null); setTapCodeCopied(false); }}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                tapMode === 'advanced'
                                  ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                                  : 'border-transparent text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <Clock className="h-3.5 w-3.5 inline mr-1" />
                              Advanced TAP
                            </button>
                          </div>

                          {/* Quick TAP Tab */}
                          {tapMode === 'quick' && (
                            <div className="p-3 bg-muted rounded-lg">
                              {quickTapResult ? (
                                <div>
                                  <div className="flex items-center text-green-600 dark:text-green-400 mb-3">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span className="font-medium">TAP Generated</span>
                                  </div>
                                  {/* TAP Code Display */}
                                  <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg p-5 text-center mb-3">
                                    <p className="text-indigo-200 text-xs uppercase tracking-widest mb-2">Access Pass Code</p>
                                    <div className="flex items-center justify-center gap-2">
                                      <p className="text-white text-3xl font-mono font-bold tracking-[0.3em]">
                                        {quickTapResult.temporaryAccessPass}
                                      </p>
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(quickTapResult.temporaryAccessPass);
                                          setTapCodeCopied(true);
                                          setTimeout(() => setTapCodeCopied(false), 3000);
                                          toast.success('TAP code copied to clipboard');
                                        }}
                                        className="text-white/80 hover:text-white transition-colors p-1"
                                        title="Copy to clipboard"
                                      >
                                        {tapCodeCopied ? <ClipboardCheck className="h-5 w-5" /> : <Clipboard className="h-5 w-5" />}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                                    <span>{quickTapResult.isUsableOnce ? 'One-time use' : 'Multi-use'}</span>
                                    <span>Expires: {new Date(quickTapResult.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <button
                                      onClick={() => { setQuickTapResult(null); setTapCodeCopied(false); }}
                                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                                    >
                                      Generate Another
                                    </button>
                                    <button
                                      onClick={() => { setShowSendTAP(false); setQuickTapResult(null); setTapCodeCopied(false); }}
                                      className="text-sm text-muted-foreground hover:text-foreground"
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm text-muted-foreground mb-3">
                                    Generate a one-time TAP to read to the user over the phone. The code will appear on screen (2 hour expiry).
                                  </p>
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => setShowSendTAP(false)}
                                      className="px-3 py-1.5 text-sm text-muted-foreground"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleGenerateTAP(true)}
                                      disabled={actionLoading}
                                      className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
                                    >
                                      {actionLoading ? (
                                        <>
                                          <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                          Generating...
                                        </>
                                      ) : (
                                        <>
                                          <Zap className="h-3.5 w-3.5 mr-1" />
                                          Generate TAP
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Advanced TAP Tab */}
                          {tapMode === 'advanced' && (
                            <div className="p-3 bg-muted rounded-lg">
                              {/* Show result if we have one (screen mode) */}
                              {quickTapResult && tapDeliveryMode === 'screen' ? (
                                <div>
                                  <div className="flex items-center text-green-600 dark:text-green-400 mb-3">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span className="font-medium">TAP Generated</span>
                                  </div>
                                  <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg p-5 text-center mb-3">
                                    <p className="text-indigo-200 text-xs uppercase tracking-widest mb-2">Access Pass Code</p>
                                    <div className="flex items-center justify-center gap-2">
                                      <p className="text-white text-3xl font-mono font-bold tracking-[0.3em]">
                                        {quickTapResult.temporaryAccessPass}
                                      </p>
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(quickTapResult.temporaryAccessPass);
                                          setTapCodeCopied(true);
                                          setTimeout(() => setTapCodeCopied(false), 3000);
                                          toast.success('TAP code copied to clipboard');
                                        }}
                                        className="text-white/80 hover:text-white transition-colors p-1"
                                        title="Copy to clipboard"
                                      >
                                        {tapCodeCopied ? <ClipboardCheck className="h-5 w-5" /> : <Clipboard className="h-5 w-5" />}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                                    <span>{quickTapResult.isUsableOnce ? 'One-time use' : 'Multi-use'}</span>
                                    <span>Expires: {new Date(quickTapResult.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <button
                                      onClick={() => { setQuickTapResult(null); setTapCodeCopied(false); }}
                                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                                    >
                                      Generate Another
                                    </button>
                                    <button
                                      onClick={() => { setShowSendTAP(false); setQuickTapResult(null); setTapCodeCopied(false); }}
                                      className="text-sm text-muted-foreground hover:text-foreground"
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              ) : tapSentResult?.success ? (
                                <div>
                                  <div className="flex items-center text-green-600 dark:text-green-400 mb-2">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span className="font-medium">TAP sent successfully!</span>
                                  </div>
                                  {tapSentResult.deliveryMethod === 'split' ? (
                                    <div className="text-sm text-muted-foreground mb-3">
                                      <p className="mb-2"><strong>TAP code</strong> sent via SMS to <strong>{tapRecipientPhone}</strong></p>
                                      <p><strong>Instructions</strong> sent via email to <strong>{tapRecipientEmail}</strong></p>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground mb-3">
                                      TAP sent to <strong>{tapRecipientEmail}</strong>.
                                    </p>
                                  )}
                                  <button
                                    onClick={() => { setShowSendTAP(false); setTapSentResult(null); }}
                                    className="text-sm text-muted-foreground hover:text-foreground"
                                  >
                                    Close
                                  </button>
                                </div>
                              ) : tapSentResult?.error ? (
                                <div>
                                  <div className="flex items-center text-red-600 dark:text-red-400 mb-2">
                                    <XCircle className="h-5 w-5 mr-2" />
                                    <span className="font-medium">Failed</span>
                                  </div>
                                  <p className="text-sm text-red-600 dark:text-red-400 mb-3">{tapSentResult.error}</p>
                                  <button
                                    onClick={() => setTapSentResult(null)}
                                    className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-md hover:bg-accent"
                                  >
                                    Try Again
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {/* Duration */}
                                  <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">Duration</label>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                      {[1, 2, 4, 8, 12, 24].map(h => (
                                        <button
                                          key={h}
                                          onClick={() => setTapDurationHours(h)}
                                          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                            tapDurationHours === h
                                              ? 'bg-indigo-600 text-white border-indigo-600'
                                              : 'bg-card text-muted-foreground border-border hover:bg-accent'
                                          }`}
                                        >
                                          {h}h
                                        </button>
                                      ))}
                                    </div>
                                    <input
                                      type="number"
                                      min={1}
                                      max={24}
                                      value={tapDurationHours}
                                      onChange={(e) => setTapDurationHours(Math.min(24, Math.max(1, parseInt(e.target.value) || 1)))}
                                      className="w-24 px-2 py-1 text-sm border border-border rounded-md bg-card text-foreground"
                                    />
                                    <span className="text-xs text-muted-foreground ml-1.5">hours</span>
                                  </div>

                                  {/* One-time use toggle */}
                                  <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">One-time use</label>
                                    <div className="flex gap-1.5">
                                      <button
                                        onClick={() => setTapOneTimeUse(true)}
                                        className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                                          tapOneTimeUse
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-card text-muted-foreground border-border hover:bg-accent'
                                        }`}
                                      >
                                        Yes
                                      </button>
                                      <button
                                        onClick={() => setTapOneTimeUse(false)}
                                        className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                                          !tapOneTimeUse
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-card text-muted-foreground border-border hover:bg-accent'
                                        }`}
                                      >
                                        No
                                      </button>
                                    </div>
                                  </div>

                                  {/* Delayed start */}
                                  <div>
                                    <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={tapDelayedStart}
                                        onChange={(e) => setTapDelayedStart(e.target.checked)}
                                        className="rounded border-border"
                                      />
                                      Delayed start
                                    </label>
                                    {tapDelayedStart && (
                                      <input
                                        type="datetime-local"
                                        value={tapStartDateTime}
                                        onChange={(e) => setTapStartDateTime(e.target.value)}
                                        className="mt-1.5 w-full px-2 py-1 text-sm border border-border rounded-md bg-card text-foreground"
                                      />
                                    )}
                                  </div>

                                  {/* Delivery mode */}
                                  <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1.5">Delivery</label>
                                    <div className="space-y-1.5">
                                      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                          type="radio"
                                          name="tapDelivery"
                                          checked={tapDeliveryMode === 'screen'}
                                          onChange={() => setTapDeliveryMode('screen')}
                                          className="border-border"
                                        />
                                        Show on Screen
                                      </label>
                                      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                        <input
                                          type="radio"
                                          name="tapDelivery"
                                          checked={tapDeliveryMode === 'send'}
                                          onChange={() => setTapDeliveryMode('send')}
                                          className="border-border"
                                        />
                                        Send to User
                                      </label>
                                    </div>
                                  </div>

                                  {/* Send fields (only when Send to User selected) */}
                                  {tapDeliveryMode === 'send' && (
                                    <div className="space-y-3 pt-1 border-t border-border">
                                      <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                          Recipient Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                          type="email"
                                          value={tapRecipientEmail}
                                          onChange={(e) => setTapRecipientEmail(e.target.value)}
                                          placeholder={user?.userPrincipalName || 'email@example.com'}
                                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:ring-2 focus:ring-indigo-500"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                          Phone for SMS <span className="text-xs font-normal">(optional)</span>
                                        </label>
                                        <input
                                          type="tel"
                                          value={tapRecipientPhone}
                                          onChange={(e) => setTapRecipientPhone(e.target.value)}
                                          placeholder={user?.mobilePhone || '+1 (555) 123-4567'}
                                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:ring-2 focus:ring-indigo-500"
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {/* Action buttons */}
                                  <div className="flex justify-end space-x-2 pt-1">
                                    <button
                                      onClick={() => setShowSendTAP(false)}
                                      className="px-3 py-1.5 text-sm text-muted-foreground"
                                    >
                                      Cancel
                                    </button>
                                    {tapDeliveryMode === 'screen' ? (
                                      <button
                                        onClick={() => handleGenerateTAP(false)}
                                        disabled={actionLoading}
                                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
                                      >
                                        {actionLoading ? (
                                          <>
                                            <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                            Generating...
                                          </>
                                        ) : 'Generate'}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={handleSendTAP}
                                        disabled={actionLoading || !tapRecipientEmail}
                                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center"
                                      >
                                        {actionLoading ? (
                                          <>
                                            <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                            Sending...
                                          </>
                                        ) : (
                                          <>
                                            <Mail className="h-3.5 w-3.5 mr-1" />
                                            Send
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Change Email / UPN */}
                    <div className="px-4 py-3 bg-card">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                            Change Email Address
                            <HelpTooltip text="Change the user's email address (UPN) to a different domain. The target domain must be verified in the tenant. This changes the user's sign-in address." />
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Change username or domain ({user.userPrincipalName?.split('@')[1]})
                          </p>
                        </div>
                        <button
                          onClick={handleOpenChangeEmail}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 inline-flex items-center"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Change
                        </button>
                      </div>

                      {showChangeEmail && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          {changeEmailResult?.success ? (
                            <div>
                              <div className="flex items-center text-green-600 dark:text-green-400 mb-2">
                                <CheckCircle className="h-5 w-5 mr-2" />
                                <span className="font-medium">Email changed successfully!</span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">{changeEmailResult.message}</p>
                              <button
                                onClick={() => { setShowChangeEmail(false); setChangeEmailResult(null); }}
                                className="text-sm text-muted-foreground hover:text-foreground"
                              >
                                Close
                              </button>
                            </div>
                          ) : changeEmailResult?.error ? (
                            <div>
                              <div className="flex items-center text-red-600 dark:text-red-400 mb-2">
                                <XCircle className="h-5 w-5 mr-2" />
                                <span className="font-medium">Failed to change email</span>
                              </div>
                              <p className="text-sm text-red-600 dark:text-red-400 mb-3">{changeEmailResult.error}</p>
                              <button
                                onClick={() => setChangeEmailResult(null)}
                                className="px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-md hover:bg-accent"
                              >
                                Try Again
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-muted-foreground mb-3">
                                Change this user's sign-in email address. The user will need to use the new address to sign in.
                              </p>
                              <div className="mb-3">
                                <label className="block text-sm font-medium text-muted-foreground mb-1">
                                  New Email Address
                                </label>
                                <div className="flex gap-1.5 items-center">
                                  <input
                                    type="text"
                                    value={newEmailUsername}
                                    onChange={(e) => setNewEmailUsername(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''))}
                                    placeholder="username"
                                    className="flex-1 px-3 py-2 border border-border rounded-md bg-card text-foreground focus:ring-2 focus:ring-blue-500 font-mono"
                                  />
                                  <span className="text-muted-foreground font-mono">@</span>
                                  {domainsLoading ? (
                                    <div className="flex-1 px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground text-sm">
                                      Loading domains...
                                    </div>
                                  ) : (
                                    <select
                                      value={newEmailDomain}
                                      onChange={(e) => setNewEmailDomain(e.target.value)}
                                      className="flex-1 px-3 py-2 border border-border rounded-md bg-card text-foreground focus:ring-2 focus:ring-blue-500 font-mono"
                                    >
                                      {availableDomains.map(d => (
                                        <option key={d.domain} value={d.domain}>
                                          {d.domain}{d.isDefault ? ' (default)' : ''}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                {newEmailUsername && newEmailDomain && (
                                  <p className="mt-1.5 text-xs text-muted-foreground">
                                    New address: <span className="font-mono font-medium text-foreground">{newEmailUsername}@{newEmailDomain}</span>
                                  </p>
                                )}
                              </div>
                              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded mb-3">
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                  The user's sign-in address will change immediately. Make sure they know their new email before proceeding.
                                </p>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => { setShowChangeEmail(false); setChangeEmailResult(null); }}
                                  className="px-3 py-1.5 text-sm text-muted-foreground"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleChangeEmail}
                                  disabled={actionLoading || !newEmailUsername || !newEmailDomain}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {actionLoading ? 'Changing...' : 'Change Email'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Enable/Disable Account */}
                    <div className="px-4 py-3 bg-card">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                            {user.accountEnabled ? 'Disable Account' : 'Enable Account'}
                            <HelpTooltip text="Blocks or allows sign-in. Disabling prevents new sign-ins but does NOT immediately sign out active sessions. For immediate sign-out, use Offboard which revokes all sessions." />
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {user.accountEnabled
                              ? 'Prevent user from signing in'
                              : 'Allow user to sign in again'}
                          </p>
                        </div>
                        <button
                          onClick={() => setShowConfirmDisable(true)}
                          className={classNames(
                            'px-3 py-1.5 text-sm rounded-md',
                            user.accountEnabled
                              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800'
                              : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                          )}
                        >
                          {user.accountEnabled ? (
                            <><XCircle className="h-4 w-4 inline mr-1" />Disable</>
                          ) : (
                            <><CheckCircle className="h-4 w-4 inline mr-1" />Enable</>
                          )}
                        </button>
                      </div>

                      {showConfirmDisable && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground mb-3">
                            {user.accountEnabled
                              ? `Are you sure you want to disable ${user.displayName}'s account? They will not be able to sign in.`
                              : `Are you sure you want to enable ${user.displayName}'s account? They will be able to sign in again.`}
                          </p>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setShowConfirmDisable(false)}
                              className="px-3 py-1.5 text-sm text-muted-foreground"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleToggleEnabled}
                              disabled={actionLoading}
                              className={classNames(
                                'px-3 py-1.5 text-sm text-white rounded-md disabled:opacity-50',
                                user.accountEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                              )}
                            >
                              {actionLoading ? 'Processing...' : (user.accountEnabled ? 'Disable' : 'Enable')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Offboard User */}
                    <div className="px-4 py-3 bg-red-50/50 dark:bg-red-900/10">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-red-900 dark:text-red-100 flex items-center gap-1">
                            Offboard User
                            <HelpTooltip text="Multi-step wizard to fully offboard: 1) Disable account & revoke sessions, 2) Reset password, 3) Remove all licenses (frees up seats), 4) Convert mailbox to shared, 5) Grant delegate access to mailbox, 6) Hide from address list, 7) Share OneDrive with delegate." />
                          </h4>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            Full offboarding workflow: disable, reset password, remove licenses, share data
                          </p>
                        </div>
                        <button
                          onClick={() => setShowOffboardWizard(true)}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 inline-flex items-center"
                        >
                          <UserMinus className="h-4 w-4 mr-1" />
                          Offboard
                        </button>
                      </div>
                    </div>

                    {/* Delete User */}
                    <div className="px-4 py-3 bg-red-100/50 dark:bg-red-900/20 border-t-2 border-red-300 dark:border-red-700">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-sm font-medium text-red-900 dark:text-red-100 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            Delete User
                            <HelpTooltip text="PERMANENTLY deletes this user account from Azure AD / Entra ID. This removes their mailbox, OneDrive, licenses, and all associated data. Deleted users go to the recycle bin for 30 days before permanent deletion." />
                          </h4>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            Permanently remove this user and all their data
                          </p>
                        </div>
                        <button
                          onClick={() => { setShowDeleteUser(true); setDeleteConfirmText(''); }}
                          className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 inline-flex items-center"
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Delete User
                        </button>
                      </div>

                      {showDeleteUser && (
                        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700 space-y-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                                Are you sure you want to permanently delete {user.displayName}?
                              </p>
                              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                This will remove their account, mailbox, OneDrive, and all data. This action cannot be undone.
                              </p>
                            </div>
                          </div>

                          {user.roles && user.roles.length > 0 && (
                            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-300 dark:border-yellow-700">
                              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-bold text-yellow-900 dark:text-yellow-100">
                                  WARNING: This user has admin roles
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                  Removing them will revoke all administrative access: {user.roles.map(r => r.displayName).join(', ')}
                                </p>
                              </div>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-medium text-red-800 dark:text-red-200 mb-1">
                              Type <span className="font-bold">DELETE</span> to confirm
                            </label>
                            <input
                              type="text"
                              value={deleteConfirmText}
                              onChange={(e) => setDeleteConfirmText(e.target.value)}
                              placeholder="DELETE"
                              className="w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setShowDeleteUser(false)}
                              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleDeleteUser}
                              disabled={actionLoading || deleteConfirmText !== 'DELETE'}
                              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 disabled:opacity-50 inline-flex items-center"
                            >
                              <UserX className="h-4 w-4 mr-1" />
                              {actionLoading ? 'Deleting...' : 'Permanently Delete User'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="bg-muted px-4 py-3 sm:px-6 border-t border-border rounded-b-lg">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Offboard Wizard Modal - must be outside Dialog */}
    {user && (
      <OffboardWizard
        clientId={clientId}
        user={user}
        isOpen={showOffboardWizard}
        onClose={() => setShowOffboardWizard(false)}
        onComplete={() => {
          loadUser();
          onUserUpdated?.();
        }}
      />
    )}

    {/* Quick Setup Wizard Modal */}
    {user && (
      <QuickSetupWizard
        clientId={clientId}
        user={user}
        isOpen={showQuickSetup}
        onClose={() => setShowQuickSetup(false)}
        onComplete={() => {
          loadUser();
          onUserUpdated?.();
        }}
      />
    )}
    </>
  );
}
